// Kit 07 · items 147 to 165, less 154 which is deferred by design.
//
//   147 settings nav      148 profile form   149 brand identity
//   150 competitor row    151 battery list   152 connector grid
//   153 member row        155 onboarding stepper (covers 156)
//   157 module tiles      158 auth card (covers 159)
//   160 session banner    161 not found      162 server error
//   163 maintenance       164 legal document 165 the canonical strings
//
// "Presentation only; the auth flows keep their current behaviour." So item
// 158 is a card that takes a form and renders it — it does not submit
// anything, does not know what a token is, and has no opinion about MFA. What
// it owns is the box, the focus order and the fact that the reset link is the
// LAST stop, after the submit control.
//
// Item 154, notification preferences, is deferred. It is not built, not
// stubbed and not sketched: "a kit with a speculative screen in it stops being
// a reference."

import type { ReactNode } from "react";
import { CircleAlert, Lock, Wrench } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Icon } from "./icon";
import { Button } from "./button";
import { Banner } from "./feedback";
import type { Tone } from "./feedback";
import { cx, stateAttr } from "./kit-state";
import type { Stateful } from "./kit-state";
import { CANONICAL } from "./strings";
import type { CanonicalString } from "./strings";

/* ═══ 147 · the settings nav ════════════════════════════════════════════════
   redline: width 220 · row 36 · gap 16 · radius 8
   keys:    "The nav keeps focus when the panel changes."

   Which is why selecting a row does not move focus into the panel: a settings
   nav that threw focus rightward on every arrow press would make arrowing
   through it impossible.
   ═════════════════════════════════════════════════════════════════════════ */

export interface SettingsNavItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  locked?: boolean;
  state?: Stateful["state"];
}

export function SettingsNav({
  label, items, value, onChange, className,
}: {
  label: string; items: SettingsNavItem[]; value: string;
  onChange?: (id: string) => void; className?: string;
}) {
  const reachable = items.filter((i) => !i.locked);

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const at = reachable.findIndex((i) => i.id === value);
    const step = event.key === "ArrowDown" ? 1 : -1;
    const next = reachable[(at + step + reachable.length) % reachable.length];
    // Selection moves; focus stays in the nav. The panel re-renders under it.
    if (next) onChange?.(next.id);
  }

  return (
    <nav className={cx("k-snav", className)} aria-label={label} onKeyDown={onKeyDown}>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className="k-snav__row"
          aria-current={item.id === value ? "page" : undefined}
          disabled={item.locked}
          onClick={() => !item.locked && onChange?.(item.id)}
          {...stateAttr(item.state)}
        >
          {item.icon ? <Icon icon={item.icon} size="sm" /> : null}
          <span className="k-snav__label">{item.label}</span>
          {/* Locked WARMS. Same rule as the nav rail and the locked notice. */}
          {item.locked ? <Icon icon={Lock} size="sm" /> : null}
        </button>
      ))}
    </nav>
  );
}

/* ═══ 148, 149 · settings panels ════════════════════════════════════════════
   148 redline: pad 20 · gap 16 · radius 8 · height 36
   148 keys:    "Save is the last stop and stays disabled until a field is
                 dirty."
   149 redline: pad 20 · gap 16 · radius 8 · square 48
   ═════════════════════════════════════════════════════════════════════════ */

export function SettingsPanel({
  title, description, children, onSave, dirty = false, saveLabel = "Save", className,
}: {
  title: string; description?: string; children: ReactNode;
  onSave?: () => void;
  /** Save stays disabled until a field is dirty. A prop rather than internal
      state, because the panel does not own the fields. */
  dirty?: boolean;
  saveLabel?: string; className?: string;
}) {
  return (
    <section className={cx("k-spanel", className)} aria-label={title}>
      <header className="k-spanel__head">
        <span className="k-spanel__title">{title}</span>
        {description ? <span className="k-meta">{description}</span> : null}
      </header>
      <div className="k-spanel__body">{children}</div>
      {onSave ? (
        // Last in the DOM, so last in the tab order.
        <div className="k-spanel__foot">
          <Button onClick={onSave} disabled={!dirty}>{saveLabel}</Button>
        </div>
      ) : null}
    </section>
  );
}

/** 149 · the brand logo square. 48, and it is the only 48 in the system. */
export function LogoSquare({
  initial, src, className,
}: { initial: string; src?: string; className?: string }) {
  return (
    <span className={cx("k-logo", className)} aria-hidden="true">
      {src ? <img className="k-logo__img" src={src} alt="" /> : initial.trim().charAt(0).toUpperCase()}
    </span>
  );
}

/* ═══ 151 · the prompt battery ══════════════════════════════════════════════
   keys: "Space toggles a prompt in or out of the next run. The count updates
          on toggle."

   The count is DERIVED from the checked set. A battery that stated "42
   prompts" beside a list where 41 were ticked is the same defect as a caption
   that disagrees with its chart.
   ═════════════════════════════════════════════════════════════════════════ */

export interface BatteryPrompt {
  id: string;
  text: string;
  included: boolean;
}

export function PromptBattery({
  title, prompts, onToggle, className,
}: {
  title: string; prompts: BatteryPrompt[];
  onToggle?: (id: string) => void; className?: string;
}) {
  const included = prompts.filter((p) => p.included).length;
  return (
    <section className={cx("k-battery", className)} aria-label={title}>
      <header className="k-battery__head">
        <span className="k-spanel__title">{title}</span>
        {/* Counted, never passed. */}
        <span className="k-meta" data-num="true" aria-live="polite">
          {included} of {prompts.length} in the next run
        </span>
      </header>
      <div className="k-battery__list" role="group">
        {prompts.map((p) => (
          <label key={p.id} className="k-battery__row">
            <input
              type="checkbox"
              className="k-battery__box"
              checked={p.included}
              onChange={() => onToggle?.(p.id)}
            />
            <span className="k-battery__text">{p.text}</span>
          </label>
        ))}
      </div>
    </section>
  );
}

/* ═══ 153 · the member row ══════════════════════════════════════════════════
   redline: pad 20 · row 52 · gap 16 · radius 8
   keys:    "A client viewer cannot open the role menu at all."

   Not disabled: absent. A disabled control tells someone their permission is
   the problem; no control tells them it is not their job. The difference
   matters on a row that lists their own name.
   ═════════════════════════════════════════════════════════════════════════ */

export type MemberRole = "Owner" | "Admin" | "Editor" | "Viewer";

export function MemberRow({
  name, email, role, canChangeRole = false, onOpenRole, state, className,
}: Stateful & {
  name: string; email: string; role: MemberRole;
  canChangeRole?: boolean; onOpenRole?: () => void; className?: string;
}) {
  return (
    <div
      role="row"
      tabIndex={0}
      className={cx("k-member", className)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && canChangeRole && onOpenRole) { e.preventDefault(); onOpenRole(); }
      }}
      {...stateAttr(state)}
    >
      <span className="k-member__text">
        <span className="k-member__name">{name}</span>
        <span className="k-meta">{email}</span>
      </span>
      {canChangeRole
        ? <Button variant="secondary" onClick={onOpenRole}>{role}</Button>
        : <span className="k-chip">{role}</span>}
    </div>
  );
}

/* ═══ 157 · the module tiles ════════════════════════════════════════════════
   keys: "Enter opens the module, which shows its own waiting state rather than
          an empty page."

   So a tile never renders a disabled state for "no data yet": the module owns
   that, and a tile that refused to open would hide the explanation.
   ═════════════════════════════════════════════════════════════════════════ */

export function ModuleTile({
  icon, title, description, locked, onOpen, className,
}: {
  icon: LucideIcon; title: string; description: string;
  locked?: boolean; onOpen?: () => void; className?: string;
}) {
  return (
    <button
      type="button"
      className={cx("k-tile", className)}
      data-locked={locked || undefined}
      onClick={onOpen}
    >
      <span className="k-tile__mark"><Icon icon={locked ? Lock : icon} /></span>
      <span className="k-tile__text">
        <span className="k-tile__title">{title}</span>
        <span className="k-tile__desc">{description}</span>
      </span>
    </button>
  );
}

/* ═══ 158, 159 · the auth card ══════════════════════════════════════════════
   redline: width 400 · pad 32 · gap 16 · radius 16
   keys:    "Focus opens in the email field. Enter submits. The reset link is
             the last stop, AFTER the submit control."

   Presentation only. The card takes the form and renders it; it submits
   nothing and knows nothing about tokens or MFA. The order of `footer` after
   `children` in the DOM is the whole of the keyboard contract.
   ═════════════════════════════════════════════════════════════════════════ */

export function AuthCard({
  title, description, children, footer, className,
}: {
  title: string; description?: string; children: ReactNode;
  /** The reset link. Rendered last, so it is the last stop. */
  footer?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx("k-auth", className)}>
      <div className="k-auth__head">
        <h1 className="k-auth__title">{title}</h1>
        {description ? <p className="k-auth__desc">{description}</p> : null}
      </div>
      {children}
      {footer ? <div className="k-auth__foot">{footer}</div> : null}
    </div>
  );
}

/* ═══ 160 · the session banner ══════════════════════════════════════════════
   keys: "A session banner takes focus once when it appears, then returns focus
          to the page."

   Which is the opposite of a toast, and deliberately: a toast confirms
   something that happened, and a session banner says the page you are looking
   at is no longer signed in. The four tones are Kit 03's banner, so there is
   one banner in the system.
   ═════════════════════════════════════════════════════════════════════════ */

export function SessionBanner({
  tone = "warn", children, action, className,
}: { tone?: Tone; children: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className="k-session" role="alert">
      <Banner tone={tone} action={action} className={className}>{children}</Banner>
    </div>
  );
}

/* ═══ 161, 162, 163 · the system pages ══════════════════════════════════════
   161 keys: The return control is the first and only stop.
   162 keys: Try again is the first stop. The reference code is SELECTABLE
             TEXT, not a control.
   163 keys: "Nothing on this page is focusable. There is no action to offer."
   ═════════════════════════════════════════════════════════════════════════ */

export function SystemPage({
  kind, title, description, action, reference, className,
}: {
  kind: "notFound" | "error" | "maintenance";
  title: string;
  description: string;
  /** 163 has none, and that is the point: there is nothing to offer. */
  action?: ReactNode;
  /** 162 only. Selectable text, never a control — someone has to be able to
      copy it into a support message. */
  reference?: string;
  className?: string;
}) {
  return (
    <div className={cx("k-system", className)} data-kind={kind} role={kind === "error" ? "alert" : "status"}>
      <span className="k-system__mark">
        <Icon icon={kind === "error" ? CircleAlert : Wrench} size="xl" />
      </span>
      <h1 className="k-system__title">{title}</h1>
      <p className="k-system__desc">{description}</p>
      {action}
      {reference ? (
        <p className="k-system__ref">
          Reference <code className="k-system__code">{reference}</code>
        </p>
      ) : null}
    </div>
  );
}

/* ═══ 164 · the legal document ══════════════════════════════════════════════
   redline: width 680 · pad 32 · gap 24 · radius 12
   keys:    "No control floats over the text."
   ═════════════════════════════════════════════════════════════════════════ */

export function LegalDocument({
  title, updated, children, className,
}: { title: string; updated?: string; children: ReactNode; className?: string }) {
  return (
    <article className={cx("k-legal", className)}>
      <header className="k-legal__head">
        <h1 className="k-legal__title">{title}</h1>
        {updated ? <span className="k-meta">{updated}</span> : null}
      </header>
      <div className="k-legal__body">{children}</div>
    </article>
  );
}

/* ═══ 165 · the canonical strings ═══════════════════════════════════════════
   keys: "The table is a scrollable region, not a control. Nothing here is
          focusable."

   Rendered from `CANONICAL` in strings.ts, which is the same constant every
   component imports. A table that listed the strings separately from the
   constants would be a sixteen-row opportunity for the two to disagree.
   ═════════════════════════════════════════════════════════════════════════ */

export function CanonicalStrings({ strings = CANONICAL, className }: {
  strings?: CanonicalString[]; className?: string;
}) {
  return (
    <div className={cx("k-strings", className)} role="region" aria-label="Canonical strings" tabIndex={0}>
      <div className="k-strings__row k-strings__row--head">
        <span>State</span>
        <span>String, used verbatim</span>
      </div>
      {strings.map((s, i) => (
        <div className="k-strings__row" key={`${s.state}-${i}`}>
          <span className="k-strings__state">{s.state}</span>
          <span className="k-strings__text">{s.text}</span>
        </div>
      ))}
    </div>
  );
}

export { CANONICAL };
